import type {HsvColor} from '@refra/modules/common/types'

export const hexToHsv = (hex: string): HsvColor => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return {h: 0, s: 0, v: 100}

  const r = parseInt(result[1], 16) / 255
  const g = parseInt(result[2], 16) / 255
  const b = parseInt(result[3], 16) / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min

  let h = 0
  const s = max === 0 ? 0 : d / max
  const v = max

  if (max !== min) {
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }

  return {h: h * 360, s: s * 100, v: v * 100}
}

export const hsvToHex = (h: number, s: number, v: number): string => {
  s /= 100
  v /= 100
  const c = v * s
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
  const m = v - c

  let r = 0,
    g = 0,
    b = 0

  if (h >= 0 && h < 60) {
    r = c
    g = x
    b = 0
  } else if (h >= 60 && h < 120) {
    r = x
    g = c
    b = 0
  } else if (h >= 120 && h < 180) {
    r = 0
    g = c
    b = x
  } else if (h >= 180 && h < 240) {
    r = 0
    g = x
    b = c
  } else if (h >= 240 && h < 300) {
    r = x
    g = 0
    b = c
  } else {
    r = c
    g = 0
    b = x
  }

  const toHex = (n: number) =>
    Math.round((n + m) * 255)
      .toString(16)
      .padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

export const calculatePopoverPosition = (
  containerRef: React.RefObject<HTMLDivElement>
): {top?: string; bottom?: string; left?: string; right?: string} => {
  if (!containerRef.current) {
    return {top: '100%', left: '0'}
  }

  const containerRect = containerRef.current.getBoundingClientRect()
  const popoverWidth = 288 // w-72 = 18rem = 288px
  const popoverHeight = 320 // approximate height of the popover
  const margin = 8

  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight

  const position: {top?: string; bottom?: string; left?: string; right?: string} = {}

  // Vertical positioning
  const spaceBelow = viewportHeight - containerRect.bottom
  const spaceAbove = containerRect.top

  if (spaceBelow >= popoverHeight + margin) {
    position.top = '100%'
  } else if (spaceAbove >= popoverHeight + margin) {
    position.bottom = '100%'
  } else {
    // Not enough space above or below, position at top of viewport with scroll
    position.top = `${-containerRect.top + margin}px`
  }

  // Horizontal positioning
  const spaceRight = viewportWidth - containerRect.left
  const spaceLeft = containerRect.right

  if (spaceRight >= popoverWidth + margin) {
    position.left = '0'
  } else if (spaceLeft >= popoverWidth + margin) {
    position.right = '0'
  } else {
    // Center it or align to viewport edge
    position.left = `${-containerRect.left + margin}px`
  }

  return position
}
